import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { AppDispatch, AppState } from '@nxsys/store';
import { aepnActions } from '@nxsys/store/slices/aepn/reducer/aepnSlice';
import { IAepnUpload, IAepnUploadRecord } from '@nxsys/types/models/aepn/upload';
import { showToast } from '@nxsys/utils/toast';
import { Descriptions, Typography } from 'antd';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { uploadRecordColumns } from './upload-record-columns';

const { Text } = Typography;

type UploadResultModalProps = {
  isOpen: boolean;
  toggle: () => void;
  uploadData: (IAepnUpload & { fileSize?: number; rawData?: { data?: { aepnResponseBody?: { aepnDataset?: IAepnUploadRecord[] } } } }) | null;
  onSaveSuccess: () => void;
};

const UploadResultModal: React.FC<UploadResultModalProps> = ({
  isOpen,
  toggle,
  uploadData,
  onSaveSuccess,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { actionLoading } = useSelector((state: AppState) => state.aepnStore);

  if (!uploadData) return null;

  const { id, fileName, fileSize, totalRecords, errorMessage } = uploadData;
  const records: IAepnUploadRecord[] = uploadData?.rawData?.data?.aepnResponseBody?.aepnDataset || [];

  const handleValidateAndSave = async () => {
    if (errorMessage) {
      showToast({ type: 'error', message: errorMessage });
      return;
    }

    try {
      await dispatch(aepnActions.aepnUploadSave({ aepnUploadIds: [id] })).unwrap();
      showToast({ type: 'info', message: 'Saving AEPN upload. Please wait...' });
      toggle();
      onSaveSuccess();
    } catch (err: any) {
      const msg = err?.message || err?.response?.data?.message || 'Save failed';
      showToast({ type: 'error', message: msg });
    }
  };

  return (
    <AppModalContainer
      title="File Information"
      isOpen={isOpen}
      toggle={toggle}
      width="90%"
      minHeight="0px"
      actions={[
        {
          name: 'Cancel',
          variant: 'outlined',
          onClick: toggle,
          disabled: actionLoading,
        },
        {
          name: 'Validate & Save',
          buttonType: 'primary',
          onClick: handleValidateAndSave,
          loading: actionLoading,
          disabled: actionLoading,
        },
      ]}
    >
      <Descriptions className="mb-4" column={3} size="small" bordered>
        <Descriptions.Item label="File Name">{fileName || '_'}</Descriptions.Item>
        {!!fileSize && (
          <Descriptions.Item label="File Size">
            {`${Math.round(fileSize / 1024)} KB`}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Total Records">{totalRecords ?? '_'}</Descriptions.Item>
        {errorMessage && (
          <Descriptions.Item label="Error" span={3}>
            <Text type="danger">{errorMessage}</Text>
          </Descriptions.Item>
        )}
      </Descriptions>

      <AppTableContainer
        rowKey={(_, idx) => String(idx)}
        data={records}
        loading={false}
        page={0}
        total={records.length}
        pageSize={records.length || 10}
        columns={uploadRecordColumns}
        scroll={{ x: 1800, y: 500 }}
        rowClassName={(record: any) => (record?.errorMessage ? 'row-error' : '')}
      />
    </AppModalContainer>
  );
};

export default UploadResultModal;
